import React from "react";
import { Col, Row } from "react-bootstrap";
import { object, number, array } from "prop-types";
import AttentionIcon from "resources/images/dynamic_score_reports/aptitude/score-report/attention-icon.png";
import MemoryIcon from "resources/images/dynamic_score_reports/aptitude/score-report/memory-icon.png";
import ReasoningIcon from "resources/images/dynamic_score_reports/aptitude/score-report/reasoning-icon.png";
import ReactionTimeIcon from "resources/images/dynamic_score_reports/aptitude/score-report/reaction-time-icon.png";
import DA_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/divided-attention-icon.png";
import VIG_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/vigilance-icon.png";
import FIL_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/filtering-icon.png";
import VWM_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/verbal-memory-icon.png";
import SWM_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/spatial-memory-icon.png";
import VIS_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/visualization-icon.png";
import LOG_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/logic-icon.png";
import IO_Icon from "resources/images/dynamic_score_reports/aptitude/score-report/information-ordering-icon.png";

const traitGroupObject = {
  Attention: {
    traitObj: {
      DA: {
        label: "Divided Attention",
        shortText: `Measures the ability to "multi-task" or concentrate on two or more things simultaneously.`,
        longText: `The Divided Attention task measures a person's ability to "multi-task" or concentrate on two or more things simultaneously. Examples of jobs for which elevated divided attention abilities are an asset include: cooks in a restaurant, airline pilots, air traffic controllers, and police officers.`,
        imgSrc: DA_Icon,
      },
      VIG: {
        label: "Selective Attention: Vigilance",
        shortText: `Measures the ability to maintain concentration on a task for a sustained period of time.`,
        longText: `The Vigilance task measures a person's ability to maintain concentration on a task for a sustained period of time. Examples of jobs for which selective attention is important include: drivers, security guards (ex. video surveillance), surgical technologists, and gaming dealers.`,
        imgSrc: VIG_Icon,
      },
      FIL: {
        label: "Selective Attention: Filtering",
        shortText: `Measures the ability to focus on important information and ignore irrelevant distractions.`,
        longText: `The Filtering task measures an individual's ability to focus on important information and ignore irrelevant distractions. Together with the Vigilance Task, Filtering provides an indication of the test subject's selective attention, the ability to sustain concentration and ignore distractions - a characteristic important to job performance in a wide variety of jobs, including drivers, security guards (ex. video surveillance), surgical technologists, and gaming dealers.`,
        imgSrc: FIL_Icon,
      },
    },
    description: `Attention, or the ability to sustain concentration, correlates with performance in a wide variety of jobs that require prolonged concentration. Examples of jobs which require elevated attention scale scores include pilots, professional drivers, chefs in restaurants, video surveillance workers, gaming dealers, skilled manufacturing workers, and more.`,
    groupImageSrc: AttentionIcon,
  },
  Memory: {
    traitObj: {
      VWM: {
        label: "Verbal Working Memory",
        shortText: `Measures the ability to remember stimuli and information.`,
        longText: `The Verbal Working Memory test measures the ability to remember stimuli and information. Working memory has been shown to be highly correlated to general intelligence, and is required in a variety of jobs. Examples of jobs for which working memory is important include: Chief Executives, HR managers, Network and Computer Systems Administrators, travel guides, and radio announcers.`,
        imgSrc: VWM_Icon,
      },
      SWM: {
        label: "Spatial Working Memory",
        shortText: `Measures the ability to "hold in mind" stimuli and information.`,
        longText: `The Spatial Working Memory test measures the ability to "hold in mind" stimuli and information. Together with verbal Working Memory, this task measures memory and memorization abilities. Examples of jobs for which working memory is important include: Chief Executives, HR managers, Network and Computer Systems Administrators, travel guides, and radio announcers.`,
        imgSrc: SWM_Icon,
      },
    },
    description: `Memory is highly correlated to general intelligence, or cognitive aptitude, and is an important asset for a wide variety of jobs. Positions for which elevated memory skills are an asset include CEOs, managers, engineers, network and computer systems administrators, and more.`,
    groupImageSrc: MemoryIcon,
  },
  Reasoning: {
    traitObj: {
      VIS: {
        label: "Visualization",
        shortText: `Measures spatial reasoning ability, a cognitive competency useful in fields such as architecture, design, and engineering.`,
        longText: `The Visualization test measures spatial reasoning ability, or "visual thinking." Spatial reasoning consists of the ability to "think in pictures" and conceptualize solutions to complex problems by relying on visualization. Spatial reasoning is particularly important in fields such as design, architecture, engineering, science, mathematics, and art.`,
        imgSrc: VIS_Icon,
      },
      LOG: {
        label: "Logic",
        shortText: `Measures deductive reasoning abilities, the ability to infer conclusions based on certain facts.`,
        longText: `The Logic test measures deductive reasoning abilities, the ability to infer conclusions based on certain facts. Because deductive reasoning is closely related to cognitive aptitude in general, the Logic Task is highly correlated to general intelligence, which is an accurate predictor of performance at jobs of all kinds.`,
        imgSrc: LOG_Icon,
      },
      IO: {
        label: "Information Ordering",
        shortText: `Measures ability to process and synthesize information in a rapid fashion.`,
        longText: `The Information Ordering test measures a person's ability to process and synthesize information in a rapid fashion, an ability that is related to generalized reasoning ability.`,
        imgSrc: IO_Icon,
      },
    },
    description: `The Reasoning Scale performance is highly correlated with general intelligence, and can serve to predict competency for a wide variety of jobs in which critical thinking and problem solving are important. These include managers, engineers, scientists, lawyers, physicians, and many more.`,
    groupImageSrc: ReasoningIcon,
  },
};

// MRAB(14) and CAST(23)
const CASTandMRAB = (props) => {
  // ---------------------------- Prop Deconstruction ---------------------------
  const {
    SelectedScoreRangeSection,
    SubcategoryScoreColumns,
    testId,
    testScores,
  } = props;
  // ---------------------------- Result Details ----------------------------
  const getResultDetails = () => (
    <Row className="mt-4">
      <Col>
        <h3 className="reportHeader mb-3">Results Details</h3>
        <Row className="ml-1">{SubcategoryScoreColumns}</Row>
        <Row>{testId === 14 ? getMRABResultDetails() : null}</Row>
        <hr />
        <Row>
          <Col lg={3}>{getTraitRow("Perceptual Reaction Time", "PRT")}</Col>
          <Col>
            <p className="mt-4">
              Measures the ability to recognize and respond to stimuli.
            </p>
          </Col>
          <Col lg={1} className="mr-3">
            <img
              src={ReactionTimeIcon}
              alt="Reaction Time"
              style={{
                height: "64px",
                width: "64px",
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const getMRABResultDetails = () =>
    Object.entries(traitGroupObject).map(([groupName, { traitObj }]) => (
      <Col md={4} key={groupName}>
        {Object.entries(traitObj).map(([traitAbbrev, { label, shortText }]) => (
          <div key={traitAbbrev}>
            {getTraitRow(label, traitAbbrev)}
            <p>{shortText}</p>
          </div>
        ))}
      </Col>
    ));

  // ---------------------------- Score Details ----------------------------
  const getScoreDetails = () => (
    <Row>
      <Col>
        <h3 className="reportHeader">Score Details</h3>
        {testId === 23 ? getCASTAttentionScoreDetails() : getMRABScoreDetails()}
      </Col>
    </Row>
  );

  const getCASTAttentionScoreDetails = () => (
    <div className="cast-score-detail-section">
      {Object.values(traitGroupObject.Attention.traitObj).map(
        ({ label, longText }) => (
          <div key={label}>
            <strong>{label}</strong>
            <p>{longText}</p>
          </div>
        )
      )}
      <div>
        <strong>Perceptual Reaction Time</strong>
        <p>
          The perceptual reaction time task measures one of the most basic
          cognitive processes: perceptual reaction time, or the speed at which a
          person recognizes and responds to a stimulus.
        </p>
      </div>
    </div>
  );

  const getMRABScoreDetails = () =>
    Object.entries(traitGroupObject).map(
      ([groupName, { traitObj, groupImageSrc, description }]) => (
        <>
          <Row key={groupName}>
            <Col xs={4}>
              <div className="trait-container">
                <img src={groupImageSrc} alt={groupName} />
                <h5>{groupName}</h5>
                <p>{description}</p>
              </div>
            </Col>
            <Col xs={8}>
              {Object.entries(traitObj).map(
                ([traitAbbrev, { label, longText, imgSrc }]) => (
                  <div
                    className="score-detail-mrab-trait-row mb-2"
                    key={traitAbbrev}
                  >
                    <div className="mb-3">
                      <img
                        src={imgSrc}
                        alt={groupName}
                        style={{
                          height: "64px",
                          width: "64px",
                        }}
                      />
                      <h3 className="sr-h5 ml-3">{label}</h3>
                    </div>
                    <p>{longText}</p>
                  </div>
                )
              )}
            </Col>
          </Row>
          {groupName !== "Reasoning" && <hr className="divider mt-4 mb-4" />}
        </>
      )
    );

  // ---------------------------- Helpers ----------------------------
  const getTraitRow = (traitName, traitKey) => (
    <div className="trait-score-row">
      <Row>
        <Col lg={8}>
          <div className="ml-3">{traitName}</div>
        </Col>
        <Col lg={4}>
          <div className="trait-score-row-number">
            {testScores.get(traitKey)}
          </div>
        </Col>
      </Row>
    </div>
  );

  return testScores ? (
    <div>
      {getResultDetails()}
      <hr className="divider" />
      {SelectedScoreRangeSection}
      {SelectedScoreRangeSection && <hr className="divider" />}
      {getScoreDetails()}
    </div>
  ) : null;
};

CASTandMRAB.propTypes = {
  testId: number,
  SubcategoryScoreColumns: array,
  SelectedScoreRangeSection: object,
  testScores: object,
};

export default CASTandMRAB;
